import React, { useMemo } from "react";
import clsx from "clsx";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const useStyles = createUseStyles(theme => ({
  "@keyframes slidein": {
    from: { opacity: 0, maxHeight: 0, overflowY: "hidden" },
    to: { opacity: 1, maxHeight: 200, overflowY: "auto" },
  },
  container: {
    flex: 1,
    "max-width": "300px",
    "background-color": theme.colorBackground,
    "border-radius": 3,
    width: `calc(100% - 22.5px - ${theme.spacing2})`,
    left: `calc(22.5px + ${theme.spacing2})`,
    height: "min-content",
  },
  noLeftMargin: {
    left: 0,
  },
  light: {
    "background-color": theme.colorDarkGrey,
  },
  current: {
    "border-radius": 3,
    color: theme.textColor,
    padding: `2px ${theme.spacing2}`,
    "min-height": 25,
    cursor: "pointer",
    width: `calc(100% - ${theme.spacing3 * 2})`,
    display: "flex",
    "flex-direction": "row",
    "justify-content": "space-between",
    "align-items": "center",
    "& svg": {
      "margin-left": 8,
    },
  },
  options: {
    "border-radius": [0, 0, 3, 3],
    animation: "$slidein ease 0.2s forwards",
    color: theme.textColor,
    "overflow-x": "hidden",
    margin: [2, 0, 0],
    padding: `${theme.spacing1} 0`,
    "list-style": "none",
    "& li": {
      margin: `${theme.spacing1} 0`,
      padding: `${theme.spacing1} ${theme.spacing3}`,
      cursor: "pointer",
      "&:hover": {
        opacity: "0.3",
        "background-color": theme.colorBackground,
      },
      width: "initial",
    },
  },
  isTeam: {
    "background-color": "#333",
  },
  activeContainer: {
    "z-index": 5,
    "border-radius": [3, 3, 0, 0],
  },
  active: {
    "border-radius": [3, 3, 0, 0],
  },
  span: {
    width: "100%",
  },
}));

const getLabel = (options, value) => {
  const result = options.find(item => item.value === value);
  if (result !== undefined) {
    return result.label;
  }

  return null;
};

const Selector = ({
  emptyPlaceholder,
  options,
  value,
  onChange,
  loading,
  light,
  noLeftMargin,
  isTeamSelect,
  expanded: dropped,
  toggle,
  span,
  shouldSortOpts = true,
}) => {
  const classes = useStyles();

  const containerClass = clsx(classes.container, {
    [classes.activeContainer]: dropped,
    [classes.noLeftMargin]: noLeftMargin,
    [classes.span]: span,
  });

  const currentClass = clsx(classes.current, {
    [classes.light]: light === true,
    [classes.active]: dropped,
  });

  const displayOptions = useMemo(() => {
    if (!options) return [];
    if (!shouldSortOpts) return options;
    return [...options].sort(({ label: a }, { label: b }) => a.localeCompare(b));
  }, [shouldSortOpts, options]);

  if (loading === true) {
    return (
      <div className={containerClass}>
        <div className={classes.current}>Loading...</div>
      </div>
    );
  }

  if (displayOptions.length === 0) {
    return (
      <div className={containerClass}>
        <div className={currentClass}>
          {emptyPlaceholder !== undefined && emptyPlaceholder}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={currentClass} onClick={toggle}>
        {getLabel(options, value)}
        <FontAwesomeIcon icon={["fas", dropped ? "caret-up" : "caret-down"]} />
      </div>
      {dropped && (
        <ul
          className={clsx(classes.options, {
            [classes.isTeam]: isTeamSelect,
            [classes.light]: light === true,
          })}
        >
          {displayOptions
            .map(item => (
              <li
                key={item.value}
                style={item.value === value ? { color: "grey" } : {}}
                onClick={() => {
                  onChange(item.value);
                  toggle();
                }}
              >
                {item.label}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default Selector;
